import React, { useCallback, useEffect, useRef, useState } from "react";
import { Inbox, Loader2, Mail, PenSquare, Search, Send, Star } from "lucide-react";

import { listThreads } from "../api/mailboxApi";
import { formatMailDate } from "../utils/mail";
import ComposeEmailDialog from "./ComposeEmailDialog";
import ThreadView from "./ThreadView";

const BRAND = "#55C5D0";

const FOLDERS = [
  { id: "inbox", label: "Inbox", icon: Inbox },
  { id: "sent", label: "Sent", icon: Send },
  { id: "starred", label: "Starred", icon: Star },
  { id: "all", label: "All mail", icon: Mail },
];

const FAILURES = {
  reconnect_needed: "Your Gmail connection needs reconnecting. Open Connection settings and connect again.",
  read_not_granted: "Reconnect your Gmail in Connection settings to see your inbox here.",
  gmail_unavailable: "Gmail did not respond. Please try again.",
};

const names = (people) => people.map((p) => p.name || p.email).join(", ");

/**
 * The agent's own Gmail inside the CRM: folders, search, conversations, and
 * reply / forward. Everything is read live from Gmail; nothing is kept here.
 */
export default function MailView({ ownEmail }) {
  const [folder, setFolder] = useState("inbox");
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [threads, setThreads] = useState([]);
  const [nextToken, setNextToken] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState("");
  const [draft, setDraft] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  // Each list request gets a number; only the newest one may fill the list,
  // so a slow answer for the previous folder or search never shows.
  const requestNo = useRef(0);

  const load = useCallback(async () => {
    const no = ++requestNo.current;
    setLoading(true);
    setError("");
    try {
      const page = await listThreads({ folder, q: query });
      if (no !== requestNo.current) return;
      setThreads(page.threads);
      setNextToken(page.next_page_token);
    } catch (err) {
      if (no !== requestNo.current) return;
      setThreads([]);
      setError(FAILURES[err?.response?.data?.code] || "Could not load your mail. Please try again.");
    } finally {
      if (no === requestNo.current) setLoading(false);
    }
  }, [folder, query]);

  useEffect(() => {
    load();
  }, [load]);

  const loadMore = async () => {
    const no = requestNo.current;
    setLoadingMore(true);
    try {
      const page = await listThreads({ folder, q: query, pageToken: nextToken });
      if (no !== requestNo.current) return;
      setThreads((prev) => [...prev, ...page.threads.filter((t) => !prev.some((p) => p.id === t.id))]);
      setNextToken(page.next_page_token);
    } catch {
      setError("Could not load more. Please try again.");
    } finally {
      setLoadingMore(false);
    }
  };

  const openThread = (id) => {
    setSelected(id);
    // Opening marks it read in Gmail; show that straight away.
    setThreads((prev) => prev.map((t) => (t.id === id ? { ...t, unread: false } : t)));
  };

  const onSent = () => {
    if (draft?.replyTo) setReloadKey((k) => k + 1);
    load();
  };

  return (
    <div className="flex h-[calc(100vh-11rem)] min-h-[28rem] overflow-hidden rounded-lg border border-gray-200 bg-white">
      {/* Folders */}
      <nav className={`w-44 shrink-0 border-r border-gray-200 p-3 ${selected ? "hidden lg:block" : "hidden sm:block"}`}>
        <button type="button" onClick={() => setDraft({})}
          className="mb-3 inline-flex w-full items-center justify-center gap-2 rounded-md px-3 py-2 text-sm text-white"
          style={{ backgroundColor: BRAND }}>
          <PenSquare className="h-4 w-4" /> New email
        </button>
        {FOLDERS.map((f) => (
          <button key={f.id} type="button" onClick={() => { setFolder(f.id); setSelected(""); }}
            aria-current={folder === f.id ? "page" : undefined}
            className={`mb-0.5 flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm ${
              folder === f.id ? "bg-[#55C5D0]/10 font-medium text-gray-900" : "text-gray-600 hover:bg-gray-50"}`}>
            <f.icon className="h-4 w-4" /> {f.label}
          </button>
        ))}
      </nav>

      {/* Conversation list */}
      <section className={`flex min-w-0 flex-col border-r border-gray-200 md:w-80 lg:w-96 md:shrink-0 ${
        selected ? "hidden md:flex" : "flex flex-1 md:flex-none"}`}>
        <form role="search" className="flex items-center gap-2 border-b border-gray-200 p-3"
          onSubmit={(e) => { e.preventDefault(); setSelected(""); setQuery(search.trim()); }}>
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
            <input type="search" aria-label="Search mail" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search mail"
              className="w-full rounded-md border border-gray-300 py-2 pl-8 pr-2 text-sm focus:border-[#55C5D0] focus:outline-none" />
          </div>
          <select aria-label="Folder" value={folder} onChange={(e) => { setFolder(e.target.value); setSelected(""); }}
            className="rounded-md border border-gray-300 px-2 py-2 text-sm sm:hidden">
            {FOLDERS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
          </select>
          <button type="button" onClick={() => setDraft({})} aria-label="New email"
            className="rounded-md p-2 text-white sm:hidden" style={{ backgroundColor: BRAND }}>
            <PenSquare className="h-4 w-4" />
          </button>
        </form>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {loading ? (
            <p className="flex items-center gap-2 p-4 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading mail…
            </p>
          ) : error ? (
            <div className="p-4 text-sm">
              <p role="alert" className="text-red-600">{error}</p>
              <button type="button" onClick={load} className="mt-2 text-gray-700 underline">Try again</button>
            </div>
          ) : threads.length === 0 ? (
            <p className="p-4 text-sm text-gray-500">{query ? "No emails match your search." : "Nothing here."}</p>
          ) : (
            <ul>
              {threads.map((t) => (
                <li key={t.id}>
                  <button type="button" onClick={() => openThread(t.id)}
                    className={`w-full border-b border-gray-100 px-4 py-3 text-left hover:bg-gray-50 ${
                      selected === t.id ? "bg-[#55C5D0]/10" : ""}`}>
                    <div className="flex items-baseline justify-between gap-2">
                      <span className={`truncate text-sm ${t.unread ? "font-semibold text-gray-900" : "text-gray-700"}`}>
                        {folder === "sent" ? `To: ${names(t.to)}` : names(t.senders)}
                        {t.message_count > 1 && <span className="ml-1 text-xs text-gray-400">{t.message_count}</span>}
                      </span>
                      <span className="shrink-0 text-xs text-gray-500">{formatMailDate(t.last_at)}</span>
                    </div>
                    <p className={`truncate text-sm ${t.unread ? "font-semibold text-gray-900" : "text-gray-800"}`}>
                      {t.subject || "(no subject)"}
                    </p>
                    <p className="truncate text-xs text-gray-500">{t.snippet}</p>
                  </button>
                </li>
              ))}
              {nextToken && (
                <li className="p-3 text-center">
                  <button type="button" onClick={loadMore} disabled={loadingMore}
                    className="inline-flex items-center gap-2 text-sm text-gray-700 underline disabled:opacity-60">
                    {loadingMore && <Loader2 className="h-4 w-4 animate-spin" />} Load older
                  </button>
                </li>
              )}
            </ul>
          )}
        </div>
      </section>

      {/* Reading pane */}
      <section className={`min-w-0 flex-1 ${selected ? "block" : "hidden md:block"}`}>
        {selected ? (
          <ThreadView threadId={selected} ownEmail={ownEmail} reloadKey={reloadKey}
            onBack={() => setSelected("")} onCompose={setDraft} />
        ) : (
          <div className="flex h-full items-center justify-center p-6 text-sm text-gray-400">
            Select a conversation to read it.
          </div>
        )}
      </section>

      <ComposeEmailDialog open={Boolean(draft)} initial={draft || undefined}
        onClose={() => setDraft(null)} onSent={onSent} />
    </div>
  );
}
